import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const AwayMessageSettings = () => {
  const [config, setConfig] = useState({
    enabled: true,
    message: '',
    use_default: true
  });
  const [preview, setPreview] = useState('');
  const [nextOpening, setNextOpening] = useState('');
  const [hasScheduling, setHasScheduling] = useState(false);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/whatsapp/away-message`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setConfig({
        enabled: response.data.enabled,
        message: response.data.message || '',
        use_default: response.data.use_default
      });
      setPreview(response.data.preview || response.data.message);
      setNextOpening(response.data.next_opening || '');
      setHasScheduling(response.data.has_scheduling || false);
      setVariables(response.data.variables || []);
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
      setMessage('Erro ao carregar configuração');
    } finally {
      setLoading(false);
    }
  };

  const handleMessageChange = (e) => {
    const newMessage = e.target.value;
    setConfig({ ...config, message: newMessage, use_default: false });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.put(`${API}/whatsapp/away-message`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage('✅ Configuração salva com sucesso!');
      setTimeout(() => setMessage(''), 3000);
      await loadConfig();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      setMessage('❌ Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm('Resetar para mensagem padrão?')) {
      try {
        setSaving(true);
        const token = localStorage.getItem('token');
        
        const response = await axios.post(`${API}/whatsapp/away-message/reset`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setConfig({ 
          enabled: true,
          message: '',
          use_default: true
        });
        setPreview(response.data.preview);
        setMessage('✅ Mensagem resetada para o padrão!');
        setTimeout(() => setMessage(''), 3000);
      } catch (error) {
        console.error('Erro ao resetar:', error);
        setMessage('❌ Erro ao resetar');
      } finally {
        setSaving(false);
      }
    }
  };

  const insertVariable = (variable) => {
    const varName = variable.split(' - ')[0];
    const newMessage = config.message + varName;
    setConfig({ ...config, message: newMessage, use_default: false });
  };

  if (loading) {
    return <div className="text-center py-8">Carregando...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow p-6 max-w-4xl mx-auto" data-testid="away-message-settings">
      <h2 className="text-2xl font-bold mb-6">😴 Mensagem de Ausência (Loja Fechada)</h2>
      
      {message && (
        <div className={`mb-4 p-3 rounded ${message.includes('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {message}
        </div>
      )}

      <div className="mb-6 p-4 bg-amber-50 border-l-4 border-amber-500 rounded">
        <h3 className="font-semibold text-amber-900 mb-2">⚠️ Quando essa mensagem é enviada?</h3>
        <p className="text-sm text-amber-800">
          Esta mensagem é enviada automaticamente quando um cliente entra em contato pelo WhatsApp 
          <strong> fora do horário de funcionamento</strong> da sua loja. O cliente recebe essa mensagem 
          em vez da saudação normal, e a IA <strong>não responde</strong> quando a loja está fechada.
        </p>
        {nextOpening && (
          <p className="text-sm text-amber-800 mt-2">
            <strong>🕐 Próxima abertura:</strong> {nextOpening}
          </p>
        )}
      </div>

      <div className="mb-6">
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
            className="w-5 h-5"
            data-testid="away-message-enabled-checkbox"
          />
          <span className="font-medium">Habilitar mensagem de ausência automática</span>
        </label>
        <p className="text-sm text-gray-600 mt-2">
          Quando habilitada, essa mensagem será enviada quando um cliente entrar em contato com a loja fechada.
        </p>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <label className="block font-medium">Mensagem Personalizada:</label>
          <label className="flex items-center space-x-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={config.use_default}
              onChange={(e) => setConfig({ ...config, use_default: e.target.checked, message: '' })}
              className="w-4 h-4"
              data-testid="use-default-checkbox"
            />
            <span>Usar mensagem padrão</span>
          </label>
        </div>
        <textarea
          value={config.message}
          onChange={handleMessageChange}
          disabled={!config.enabled || config.use_default}
          rows="6"
          maxLength="500"
          className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-100"
          placeholder="Digite sua mensagem de ausência..."
          data-testid="away-message-textarea"
        />
        <div className="flex justify-between items-center mt-2 text-sm text-gray-600">
          <span>{config.message.length}/500 caracteres</span>
        </div>
      </div>

      <div className="mb-6">
        <label className="block font-medium mb-2">📝 Variáveis Disponíveis:</label>
        <div className="flex flex-wrap gap-2">
          {variables.map((variable, index) => (
            <button
              key={index}
              onClick={() => insertVariable(variable)}
              disabled={!config.enabled || config.use_default}
              className="px-3 py-1 bg-blue-100 text-blue-800 rounded-md text-sm hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {variable}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Clique para inserir a variável na mensagem. Ela será substituída automaticamente pelo valor real.
        </p>
      </div>

      <div className="mb-6">
        <label className="block font-medium mb-2">👁️ Preview:</label>
        <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 whitespace-pre-wrap" data-testid="away-message-preview">
          {preview || 'Mensagem padrão será usada...'}
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={handleSave}
          disabled={!config.enabled || saving}
          className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          data-testid="save-away-message-btn"
        >
          {saving ? '⏳ Salvando...' : '💾 Salvar Alterações'}
        </button>
        
        <button
          onClick={handleReset}
          disabled={saving}
          className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="reset-away-message-btn"
        >
          🔄 Usar Padrão
        </button>
      </div>

      <div className="mt-6 p-4 bg-emerald-50 border-l-4 border-emerald-500 rounded">
        <h3 className="font-semibold text-blue-900 mb-2">ℹ️ Como funciona:</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• A mensagem é enviada quando a loja está <strong>fechada</strong> (baseado no horário configurado)</li>
          <li>• A IA <strong>não responde</strong> enquanto a loja estiver fechada</li>
          <li>• O cliente é informado sobre o próximo horário de abertura</li>
          {hasScheduling && (
            <li>• 📅 Seu restaurante tem <strong>agendamento habilitado</strong> - a mensagem padrão sugere pedidos agendados</li>
          )}
          <li>• Use as variáveis para personalizar a mensagem automaticamente</li>
          <li>• Máximo de 500 caracteres</li>
        </ul>
      </div>
    </div>
  );
};

export default AwayMessageSettings;
